/* eslint-disable react-hooks/exhaustive-deps */
import {
  BarcodeOutlined,
  CheckOutlined,
  LoadingOutlined,
  MinusOutlined,
  PictureOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { message, Skeleton, Typography } from 'antd';
import clsx from 'clsx';
import { uniqBy } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useScanCapture } from '../../hooks/useScanCapture';
import { getProductDetails, getProductsWithPagination, scanProduct } from '../../redux/actions/products';
import { formatCurrency, getApplicableTierPrice, getProductImageUrl } from '../../utils';
import { kioskErrorToast } from '../../utils/toast';
import DevelopmentOptions from './DevelopmentOptions';
import ScannedProductModal from './ScannedProductModal';

const { Paragraph } = Typography;

const KioskSkeletonCard = () => (
  <div className="kiosk-product-card kiosk-skeleton-card" style={{ pointerEvents: 'none' }}>
    <div className="image-wrapper">
      <Skeleton.Input active size="small" className="skeleton-code-badge" />
      <Skeleton.Node active style={{ width: 60, height: 60, borderRadius: 8 }}>
        <PictureOutlined style={{ fontSize: 28, color: '#d1d5db' }} />
      </Skeleton.Node>
    </div>

    <div className="product-info">
      <div className="product-name" style={{ minHeight: '34px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', gap: '5px' }}>
        <Skeleton.Input active size="small" style={{ width: '100%', height: 13, minWidth: 'unset', borderRadius: 4 }} />
        <Skeleton.Input active size="small" style={{ width: '50%', height: 13, minWidth: 'unset', borderRadius: 4 }} />
      </div>

      <div className="price-row">
        <div className="price-details" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Skeleton.Input active size="small" style={{ width: 44, height: 16, minWidth: 'unset', borderRadius: 4 }} />
          <Skeleton.Input active size="small" style={{ width: 28, height: 12, minWidth: 'unset', borderRadius: 4 }} />
        </div>
      </div>

      <div className="card-action-row">
        <div className="card-pre-qty" style={{ border: '1px solid #e5e7eb', background: '#f3f4f6', padding: '2px' }}>
          <Skeleton.Input active size="small" style={{ width: '100%', height: 22, minWidth: 'unset', borderRadius: 6 }} />
        </div>
        <Skeleton.Button active size="small" style={{ width: 28, height: 28, minWidth: 28, borderRadius: 8 }} />
      </div>
    </div>
  </div>
);

const ProductCard = React.memo(
  ({ product: initialProduct, onAddToCart, isEntering }) => {
    const [product, setProduct] = useState(() => ({
      ...initialProduct,
      unit_price: initialProduct?.unit_price,
    }));
    const [quantity, setQuantity] = useState(initialProduct?.quantity ?? 1);
    const [justAdded, setJustAdded] = useState(null);
    const addedTimeoutRef = useRef(null);
    const fetchTiersPromise = useRef(null);

    useEffect(() => {
      return () => {
        if (addedTimeoutRef.current) clearTimeout(addedTimeoutRef.current);
      };
    }, []);

    const fetchPriceLevels = async (currentProd = product) => {
      if (currentProd?.tiers) return currentProd.tiers;
      if (fetchTiersPromise.current) return fetchTiersPromise.current;

      const pId = currentProd?.product_netsuite_id || currentProd?.netsuite_id;
      if (!pId) return [];

      fetchTiersPromise.current = getProductDetails({
        id: pId,
        details: 1,
        loading: false,
      })
        .then((res) => {
          return res?.product || [];
        })
        .finally(() => {
          fetchTiersPromise.current = null;
        });

      return fetchTiersPromise.current;
    };

    const handleChangeQuantity = async (newQty) => {
      const qtyNum = typeof newQty === 'number' ? newQty : (parseInt(newQty, 10) || 1);
      const tiers = await fetchPriceLevels();

      const price = getApplicableTierPrice({
        ...product,
        tiers,
        uom: product?.uom || 'Case',
        product_case_quantity: product?.case_quantity || product?.product_case_quantity || 1,
        quantity: qtyNum,
        price_level: product?.price_level,
      });

      const newUnitPrice = price?.tier_price || price?.unit_price || product?.unit_price;

      setProduct((prev) => ({
        ...prev,
        tiers,
        unit_price: newUnitPrice
      }));
    };

    const handleQtyChange = (delta) => {
      const current = quantity !== '' ? Number(quantity) : 1;
      const next = Math.min(999, Math.max(1, current + delta));
      setQuantity(next);
      handleChangeQuantity(next);
    };

    const handleQtyInput = (value) => {
      if (value === '') {
        setQuantity('');
      } else {
        const num = parseInt(value, 10);
        if (!isNaN(num)) {
          const clamped = Math.min(999, Math.max(1, num));
          setQuantity(clamped);
          handleChangeQuantity(clamped);
        }
      }
    };

    const handleAdd = async (e) => {
      e.stopPropagation();
      if (justAdded === 'loading') return;
      const qty = quantity !== '' ? Math.min(999, Math.max(1, Number(quantity))) : 1;

      setJustAdded('loading');
      try {
        let productToAdd = product;
        const tiers = await fetchPriceLevels();
        if (tiers?.length) {
          const price = getApplicableTierPrice({
            ...product,
            tiers,
            uom: product?.uom || 'Case',
            product_case_quantity: product?.case_quantity || product?.product_case_quantity || 1,
            quantity: qty,
            price_level: product?.price_level,
          });
          const calculatedPrice = price?.tier_price || price?.unit_price || product?.unit_price;
          if (calculatedPrice) {
            productToAdd = {
              ...product,
              tiers,
              unit_price: calculatedPrice,
            };
          }
        }

        let res;
        if (onAddToCart) {
          res = await onAddToCart(productToAdd, qty);
        }

        if (!res || !res.ack) {
          setJustAdded(null);
          if (res?.message) {
            message.error(res.message);
          } else {
            message.error('Failed to add product to cart');
          }
          return;
        }

        setJustAdded('added');
        setQuantity(1);

        if (addedTimeoutRef.current) {
          clearTimeout(addedTimeoutRef.current);
        }
        addedTimeoutRef.current = setTimeout(() => {
          setJustAdded(null);
        }, 1500);
      } catch (error) {
        console.error('Failed to add to cart:', error);
        setJustAdded(null);
        message.error(error?.message || 'Failed to add product to cart');
      }
    };

    const productImage = getProductImageUrl(product);
    const price = product.unit_price;
    const isTaxable = product.taxable === 'Taxable' || product.vat;

    return (
      <div
        className={clsx('kiosk-product-card', {
          'is-entering': isEntering,
        })}
      >
        <div className="image-wrapper">
          {product.code && (
            <div
              className="product-code-badge"
              title={product.code}
            >
              <span>
                {product.code}
              </span>
            </div>
          )}
          {productImage ? (
            <img
              src={productImage}
              alt={product.name}
              className="product-image"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div
            className="image-placeholder"
            style={{ display: productImage ? 'none' : 'flex' }}
          >
            <PictureOutlined />
          </div>
        </div>

        <div className="product-info">
          <div className="product-name">
            <Paragraph
              ellipsis={{
                rows: 2,
                tooltip: true,
              }}
              style={{ margin: 0, color: 'inherit', fontWeight: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
            >
              {product.name}
            </Paragraph>
          </div>
          <div className="price-row">
            <div className="price-details">
              {price ? (
                <div className="product-price">{formatCurrency(price)}</div>
              ) : (
                <div />
              )}

              {isTaxable && <span className="vat-label">+VAT</span>}
            </div>
          </div>

          <div className="card-action-row" onClick={(e) => e.stopPropagation()}>
            <div className="card-pre-qty">
              <button
                type="button"
                className="pre-qty-btn"
                disabled={justAdded === 'loading'}
                onClick={(e) => {
                  e.stopPropagation();
                  handleQtyChange(-1);
                }}
                title="Decrease quantity"
              >
                <MinusOutlined style={{ fontSize: '10px' }} />
              </button>
              <input
                type="number"
                min="1"
                max="999"
                className="pre-qty-input"
                value={quantity}
                disabled={justAdded === 'loading'}
                onChange={(e) => handleQtyInput(e.target.value)}
                onBlur={() => {
                  if (!quantity || quantity < 1) {
                    setQuantity(1);
                    handleChangeQuantity(1);
                  } else if (Number(quantity) > 999) {
                    setQuantity(999);
                    handleChangeQuantity(999);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
              />
              <button
                type="button"
                className="pre-qty-btn"
                disabled={justAdded === 'loading'}
                onClick={(e) => {
                  e.stopPropagation();
                  handleQtyChange(1);
                }}
                title="Increase quantity"
              >
                <PlusOutlined style={{ fontSize: '10px' }} />
              </button>
            </div>

            <button
              type="button"
              className={clsx('card-add-btn', {
                added: justAdded === 'added',
                loading: justAdded === 'loading',
              })}
              onClick={handleAdd}
              disabled={justAdded === 'loading'}
              title="Add to cart"
            >
              {justAdded === 'loading' ? (
                <LoadingOutlined style={{ fontSize: '13px' }} />
              ) : justAdded === 'added' ? (
                <CheckOutlined style={{ fontSize: '13px' }} />
              ) : (
                <PlusOutlined style={{ fontSize: '13px' }} />
              )}
            </button>
          </div>
        </div>
      </div>
    );
  });

const getScannedUom = (barcode, product) => {
  if (barcode && product) {
    if (barcode === product.product_item_barcode) return 'Each';
    if (barcode === product.product_carton_barcode || barcode === product.code) return 'Case';
  }
  return product?.uom || 'Case';
};

const isSameProduct = (p, c, checkUom = false, barcode = null) => {
  const pId = p.netsuite_id ?? p.product_netsuite_id ?? p.item_internal_id ?? p.id;
  const cId = c.product_netsuite_id ?? c.netsuite_id ?? c.item_internal_id;
  const idMatch = pId !== undefined && cId !== undefined && String(pId) === String(cId);

  if (!idMatch || !checkUom) return idMatch;

  const pUom = getScannedUom(barcode, p);
  const cUom = c.uom || 'Case';

  return pUom.toLowerCase() === cUom.toLowerCase();
};

const ProductGrid = ({
  customerId,
  isCustomerLoading,
  selectedCategories,
  activeCategory,
  searchTerm,
  addToCart,
  getOrCreateOrder,
  cartItems = [],
  recentlyRemovedProduct,
  updateQuantity,
  disableScanning = false,
}) => {
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [scannedProduct, setScannedProduct] = useState(null);
  const [scannedBarcode, setScannedBarcode] = useState('');
  const [isScannedModalOpen, setIsScannedModalOpen] = useState(false);
  const scannedModalRef = useRef(null);

  const [recentlyEnteredId, setRecentlyEnteredId] = useState(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (!recentlyRemovedProduct?.product) return;

    const item = recentlyRemovedProduct.product;
    const {
      id,
      order_id,
      customer_netsuite_id,
      product_netsuite_id,
      product_name,
      product_code,
      quantity,
      purchase_price,
      vat,
      posted_by,
      posted_at,
      createdAt,
      updatedAt,
      ...newProduct
    } = item;

    const normalized = {
      ...newProduct,
      netsuite_id: product_netsuite_id || newProduct.netsuite_id,
      name: product_name || newProduct.name,
      code: product_code || newProduct.code,
      taxable: vat > 0 || newProduct.taxable === 'Taxable' ? 'Taxable' : 'Non-taxable',
    };

    const pId = String(
      normalized.netsuite_id || normalized.product_netsuite_id
    );
    setRecentlyEnteredId(pId);

    setAllProducts((prev) =>
      uniqBy([normalized, ...prev], (p) => p.netsuite_id || p.product_netsuite_id || p.id)
    );

    // Scroll smoothly to top so user sees the returned product enter at the start
    const scrollContainer = document.querySelector('.kiosk-content-area');
    if (scrollContainer && scrollContainer.scrollTop > 50) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
    }

    const timer = setTimeout(() => {
      setRecentlyEnteredId(null);
    }, 400);

    return () => clearTimeout(timer);
  }, [recentlyRemovedProduct]);

  // Filter out products currently in cart from the listing
  const displayedProducts = useMemo(() => {
    return allProducts.filter((p) => !cartItems?.some((c) => isSameProduct(p, c)));
  }, [allProducts, cartItems]);

  const categoriesList = useMemo(
    () => (Array.isArray(selectedCategories)
      ? selectedCategories
      : (Array.isArray(activeCategory)
        ? activeCategory
        : (activeCategory ? [activeCategory] : ['All Products', 'All Product']))),
    [selectedCategories, activeCategory]
  );

  const isAllCategory =
    categoriesList.length === 0 ||
    categoriesList.some(
      (c) =>
        c.trim().toLowerCase() === 'all product' ||
        c.trim().toLowerCase() === 'all products'
    );

  const fetchProducts = useCallback(
    async (currentPage, isAppend = false) => {
      if (!customerId) return;
      if (!isAppend) setLoading(true);
      else setLoadingMore(true);

      try {
        const body = {
          customer_id: customerId,
          page: currentPage,
          pageSize: 50,
          order_type: 1
        };

        const isSearchActive = !!(searchTerm && searchTerm.trim());

        if (!isAllCategory && !isSearchActive) {
          body.category = categoriesList;
        }

        if (isSearchActive) {
          body.searchText = searchTerm.trim();
        }

        const response = await getProductsWithPagination({
          body,
          isDispatch: false,
        });

        if (response?.ack) {
          const fetchedProducts = response.products || [];
          setAllProducts((prev) =>
            isAppend
              ? uniqBy([...prev, ...fetchedProducts], (p) => p.netsuite_id || p.product_netsuite_id || p.id)
              : fetchedProducts
          );

          setTotalPages(response.has_more ? currentPage + 1 : currentPage);
        } else {
          message.error('Failed to load products');
        }
      } catch (error) {
        console.error(error);
        message.error('Something went wrong fetching products');
      } finally {
        if (!isAppend) setLoading(false);
        else setLoadingMore(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categoriesList, isAllCategory, searchTerm, customerId]
  );

  // Debounced reset and fetch on filter changes
  useEffect(() => {
    if (isCustomerLoading) return;

    if (!customerId) {
      setLoading(false);
      return;
    }

    setPage(1);
    setAllProducts([]);
    setLoading(true);

    const timer = setTimeout(() => {
      fetchProducts(1, false);
    }, 300);

    return () => clearTimeout(timer);
  }, [fetchProducts, customerId, isCustomerLoading]);

  // Load next page
  const loadMore = useCallback(() => {
    if (page < totalPages && !loadingMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchProducts(nextPage, true);
    }
  }, [page, totalPages, loadingMore, loading, fetchProducts]);

  // Infinite scroll listener on .kiosk-content-area
  useEffect(() => {
    const scrollContainer = document.querySelector('.kiosk-content-area');
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      if (scrollTop + clientHeight >= scrollHeight - 300) {
        loadMore();
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [loadMore]);

  // Auto-load more if content doesn't fill the screen (e.g. after items are filtered out because they were added to the cart)
  useEffect(() => {
    if (loading || loadingMore || page >= totalPages) return;

    const scrollContainer = document.querySelector('.kiosk-content-area');
    if (!scrollContainer) return;

    const timer = setTimeout(() => {
      const { scrollHeight, clientHeight } = scrollContainer;
      if (scrollHeight <= clientHeight + 300) {
        loadMore();
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [displayedProducts.length, loading, loadingMore, page, totalPages, loadMore]);


  // Barcode scanner listener
  const handleScannedCode = async (trimmed) => {
    // If the scanned barcode belongs to the currently displayed product, just bump the quantity/UOM
    if (
      isScannedModalOpen &&
      scannedProduct &&
      (trimmed === scannedProduct.product_item_barcode ||
        trimmed === scannedProduct.product_carton_barcode ||
        trimmed === scannedProduct.code)
    ) {
      if (scannedModalRef.current?.handleScanAgain) {
        scannedModalRef.current.handleScanAgain(trimmed);
      }
      setScannedBarcode(trimmed);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    let product = null;

    try {
      let currentOrderId = null;
      if (getOrCreateOrder) {
        currentOrderId = await getOrCreateOrder();
      }

      const res = await scanProduct({
        barcode: trimmed,
        customer_netsuite_id: customerId,
        ...(currentOrderId && { order_id: currentOrderId })
      }, { loading: false, signal: controller.signal });

      if (res === 'ERR_CANCELED' || controller.signal.aborted) {
        return;
      }

      if (res?.ack) {
        product = res?.product?.[0];
      }
    } catch (err) {
      if (err?.code !== 'ERR_CANCELED' && !controller.signal.aborted) {
        console.error('Scan API lookup error:', err);
      }
    }

    if (product) {
      if (isScannedModalOpen && scannedModalRef.current) {
        await scannedModalRef.current.handleAutoAdd();
      }
      setScannedProduct(product);
      setScannedBarcode(trimmed);
      setIsScannedModalOpen(true);
    } else if (!controller.signal.aborted) {
      kioskErrorToast("Product Not Found", "Scanned barcode was not recognized", <BarcodeOutlined />);
    }
  };

  useScanCapture(handleScannedCode, { enabled: !disableScanning });

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Initial skeleton loading state
  if (loading && allProducts?.length === 0) {
    return (
      <div className="product-grid-container">
        {Array.from({ length: 18 }).map((_, i) => (
          <KioskSkeletonCard key={`init-skeleton-${i}`} />
        ))}
      </div>
    );
  }

  return (
    <>
      <DevelopmentOptions onSimulateScan={handleScannedCode} />

      {displayedProducts.length === 0 && !loadingMore ? (
        <div className="product-grid-empty">No products found.</div>
      ) : (
        <div className="product-grid-container">
          {displayedProducts.map((product) => {
            const itemId = String(
              product.netsuite_id ??
              product.product_netsuite_id ??
              product.item_internal_id ??
              product.id
            );
            return (
              <ProductCard
                key={itemId}
                product={product}
                onAddToCart={addToCart}
                isEntering={recentlyEnteredId === itemId}
              />
            );
          })}

          {/* Skeleton loading cards when fetching next page */}
          {loadingMore &&
            Array.from({ length: 6 }).map((_, i) => (
              <KioskSkeletonCard key={`more-skeleton-${i}`} />
            ))}
        </div>
      )}

      {/* Scanned Product Detail & Quantity Modal */}
      <ScannedProductModal
        ref={scannedModalRef}
        product={scannedProduct}
        barcode={scannedBarcode}
        isOpen={isScannedModalOpen}
        initialQuantity={(() => {
          if (!scannedProduct) return 1;
          const cartItem = cartItems?.find((c) => isSameProduct(scannedProduct, c, true, scannedBarcode));
          return cartItem ? Number(cartItem.quantity) + 1 : 1;
        })()}
        onClose={() => {
          setIsScannedModalOpen(false);
          setScannedProduct(null);
        }}
        onAddToCart={async (prod, qty) => {
          const cartItem = cartItems?.find((c) => isSameProduct(prod, c));
          if (cartItem && updateQuantity) {
            return await updateQuantity(
              cartItem.id || cartItem.netsuite_id || cartItem.product_netsuite_id,
              qty,
              cartItem,
              prod.uom
            );
          } else if (addToCart) {
            return await addToCart(prod, qty);
          }
        }}
      />
    </>
  );
};

export default ProductGrid;


